'use client'

/**
 * Avatar component — single source of truth for the user's avatar look
 * everywhere it appears (profile page editor + small nav presence).
 *
 * Fallback state (no upload yet) renders the initials over a 30°
 * cyan→deep-blue gradient using the exact brand-field colors from the
 * login screen (#0b77be → #243d98). A user who later deletes their
 * photo returns to that same signature gradient — visual continuity
 * with the very first screen they saw of the app.
 *
 * Sizes:
 *   xs  — 28 px (top nav)
 *   sm  — 40 px (compact lists, future)
 *   lg  — 128 px (profile editor)
 *
 * Editable mode (only on `lg`, gated by the parent passing `onUpload`)
 * adds a hover overlay, drag-and-drop target, upload spinner, and a
 * brief "just saved" check pulse — see the related JSDoc on each piece.
 */
import { Camera, Check, Loader2, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'

export type AvatarSize = 'xs' | 'sm' | 'lg'

const sizeClass: Record<AvatarSize, string> = {
  xs: 'h-7 w-7 text-[10px] font-bold tracking-tight',
  sm: 'h-10 w-10 text-xs font-bold tracking-tight',
  lg: 'h-32 w-32 text-3xl font-bold tracking-tight',
}

const ringClass: Record<AvatarSize, string> = {
  xs: 'ring-1',
  sm: 'ring-1',
  lg: 'ring-1',
}

interface BaseProps {
  size: AvatarSize
  /** Public URL of the uploaded avatar — null = fallback gradient. */
  src?: string | null
  /** Initials shown in the fallback. */
  initials: string
  /** Optional className for the outer wrapper. */
  className?: string
}

interface ReadOnlyProps extends BaseProps {
  onUpload?: never
  onDelete?: never
}

interface EditableProps extends BaseProps {
  size: 'lg'
  onUpload: (file: File) => Promise<void> | void
  onDelete?: () => Promise<void> | void
  /** External signal: true while a save is in-flight. */
  uploading?: boolean
  /** External signal: true briefly after a successful save. */
  justSaved?: boolean
}

type Props = ReadOnlyProps | EditableProps

export function Avatar(props: Props) {
  const { size, src, initials, className } = props
  const editable = 'onUpload' in props && size === 'lg'
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const editProps = editable ? (props as EditableProps) : null

  function pickFile() {
    fileInputRef.current?.click()
  }

  async function onSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file && editProps) {
      await editProps.onUpload(file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setDragging(false)
    const file = e.dataTransfer?.files?.[0]
    if (file && editProps) {
      await editProps.onUpload(file)
    }
  }

  // Fallback "gradient + initials" surface, used when no avatar_url is set.
  // The gradient uses the same brand-field colors as the login screen
  // (#0b77be → #243d98) so first-time users get visual continuity with
  // the screen they just came from.
  const fallback = (
    <div
      className="flex h-full w-full items-center justify-center text-white drop-shadow-sm"
      style={{
        background:
          'linear-gradient(135deg, #0b77be 0%, #1b6cc9 35%, #243d98 100%)',
      }}
    >
      {initials}
    </div>
  )

  const surface = src ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={initials}
      className="h-full w-full object-cover"
      draggable={false}
    />
  ) : (
    fallback
  )

  return (
    <div
      className={
        'group relative inline-flex items-center justify-center overflow-visible ' +
        (className ?? '')
      }
    >
      <div
        onClick={editable ? pickFile : undefined}
        onDragOver={(e) => {
          if (!editable) return
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={editable ? onDrop : undefined}
        className={[
          sizeClass[size],
          ringClass[size],
          'relative overflow-hidden rounded-full bg-slate-200 transition-all duration-200',
          editable ? 'cursor-pointer' : '',
          dragging
            ? 'ring-2 ring-secondary'
            : editable
              ? 'ring-secondary/15 hover:ring-2 hover:ring-secondary/60 hover:ring-offset-2 hover:ring-offset-slate-25'
              : 'ring-secondary/15',
          editProps?.uploading ? 'animate-pulse' : '',
        ].join(' ')}
        role={editable ? 'button' : undefined}
        aria-label={editable ? 'Change profile picture' : undefined}
      >
        {surface}

        {/* Hover-only "Change photo" overlay — only on the large size and
            only when editable. JetBrains Mono uppercase keeps it in the
            terminal-y register the rest of the researcher chrome uses. */}
        {editable && !editProps?.uploading && (
          <div
            className={
              'pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 bg-slate-900/45 text-white transition-opacity duration-150 ' +
              (dragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')
            }
          >
            <Camera className="h-5 w-5" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em]">
              {dragging ? 'Drop to upload' : 'Change photo'}
            </span>
          </div>
        )}

        {editable && editProps?.uploading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-900/55 text-white">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
      </div>

      {/* "Just saved" check pulse — top-right of the avatar. Same micro
          interaction the inline field-saved indicators use, kept here at
          the avatar level so a successful upload feels intentional. */}
      {editable && editProps?.justSaved && (
        <span className="pointer-events-none absolute -right-1 -top-1 flex h-6 w-6 animate-in fade-in zoom-in items-center justify-center rounded-full bg-secondary text-white shadow-md">
          <Check className="h-4 w-4" strokeWidth={3} />
        </span>
      )}

      {editable && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={onSelect}
          className="hidden"
        />
      )}

      {/* Remove button — visible only when an avatar is uploaded, on hover.
          Sits below the avatar so it never overlaps with the photo itself. */}
      {editable && src && editProps?.onDelete && !editProps?.uploading && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            void editProps.onDelete?.()
          }}
          className="absolute -bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-slate-500 opacity-0 shadow-sm transition-opacity hover:text-rose-500 group-hover:opacity-100"
          title="Remove photo"
        >
          <Trash2 className="h-3 w-3" />
          Remove
        </button>
      )}
    </div>
  )
}
